import { useEffect, useId, useState } from "react";
import { useAuth } from "../state/AuthContext";

export interface GazeOffset { x: number; y: number }
const ZERO: GazeOffset = { x: 0, y: 0 };
const LIMIT = 50;
const clamp = (value: number) => Math.round(Math.max(-LIMIT, Math.min(LIMIT, value)) * 10) / 10;

export function useGazeOffset(sessionId: string) {
  const { user } = useAuth();
  const key = `pads.gazeOffset.v1:${user?.id ?? "local"}:${sessionId}`;
  // ReviewWorkspace is keyed by session ID, so each recording loads its own offset.
  const [offset, setOffset] = useState<GazeOffset>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(key) ?? "null");
      if (saved && typeof saved.x === "number" && Number.isFinite(saved.x)
        && typeof saved.y === "number" && Number.isFinite(saved.y)) {
        return { x: clamp(saved.x), y: clamp(saved.y) };
      }
    } catch { /* Missing or inaccessible preferences use the original position. */ }
    return ZERO;
  });
  const [saved, setSaved] = useState(true);
  const update = (next: GazeOffset) => {
    const safe = { x: clamp(next.x), y: clamp(next.y) };
    setOffset(safe);
    try { localStorage.setItem(key, JSON.stringify(safe)); setSaved(true); }
    catch { setSaved(false); }
  };
  return { offset, update, saved };
}

function OffsetAxis({ label, hint, value, onChange }: {
  label: string; hint: string; value: number; onChange: (value: number) => void;
}) {
  const id = useId();
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  return <div className="gaze-offset-axis">
    <label htmlFor={id}>{label}</label>
    <span className="gaze-offset-value">
      <input id={id} type="number" min={-LIMIT} max={LIMIT} step={0.1} value={draft}
        onChange={event => {
          const text = event.target.value;
          setDraft(text);
          const next = Number(text);
          if (text.trim() && Number.isFinite(next) && Math.abs(next) <= LIMIT) onChange(next);
        }}
        onBlur={() => {
          const next = draft.trim() && Number.isFinite(Number(draft)) ? clamp(Number(draft)) : value;
          setDraft(String(next)); onChange(next);
        }} />
      <span aria-hidden="true">%</span>
    </span>
    <input type="range" min={-LIMIT} max={LIMIT} step={0.1} value={value}
      aria-label={`${label} slider`} aria-valuetext={`${value}%`}
      onChange={event => { setDraft(event.target.value); onChange(Number(event.target.value)); }} />
    <span className="tiny muted">{hint}</span>
  </div>;
}

export function GazeOffsetSettings({ offset, onChange, saved }: {
  offset: GazeOffset; onChange: (offset: GazeOffset) => void; saved: boolean;
}) {
  return <>
    <OffsetAxis label="Horizontal offset" hint="− Left / + Right · % of image width" value={offset.x}
      onChange={x => onChange({ ...offset, x })} />
    <OffsetAxis label="Vertical offset" hint="− Up / + Down · % of image height" value={offset.y}
      onChange={y => onChange({ ...offset, y })} />
    <button className="ghost" disabled={offset.x === 0 && offset.y === 0} onClick={() => onChange(ZERO)}>Reset offsets</button>
    <p className="tiny muted" role="status">{saved
      ? "Saved for this recording in this browser."
      : "Browser storage is unavailable. Changes apply for this visit only."} Original gaze data is unchanged.</p>
  </>;
}
